import { useQuery } from '@tanstack/react-query';
import { t } from 'i18next';
import { MDBIcon } from 'mdbreact';
import React, { useEffect, useMemo } from 'react';
import Table from 'react-bootstrap/Table';
import { Link, useHistory } from 'react-router-dom';
import axios from 'axios';
import { hostUrl } from '../api';
import { PatientReportApiAppApiClinicReportClinicianPatientsTableRequest } from '../api/generated/apis/patient-report-api';
import {
  /*
    Note: This was replaced because the invitation table also use the same response type
    if the invitation table is separated in the future and a different response type is needed,
    this will be reverted:

    AppApiClinicReportClinicianPatientsTable200,
  */

  AppApiClinicReportPatientClinicInvitationTable200Response,
  ClinicianPatientsTableResponseRow,
  ClinicResponse,
} from '../api/generated/models';
import { useStore, useStoreDispatch } from '../context';
import { Trans, useTranslation } from '../i18n';
import { PatientListFiltersType } from '../pages/patientlist';
import { ClinicService } from '../services/clinic';
import { idToStr } from '../utils/idStr';
import { clinicGetHospitalPatientCustomFields, HospitalPatientCustomField } from './addUser';
import { LoadingSpinner } from './loadingSpinner';

const InvitationStatus = {
  pending: t('Invitation Pending'),
  rejected: t('Invitation Rejected'),
  expired: t('Invitation Expired'),
  revoked: t('Invitation Revoked'),
};

type TableColumn<T> = {
  field?: keyof T,
  header: string,
  render?: (row: T) => React.ReactNode,
  onClick?: null | ((row: T) => void),
  hidden?: boolean,
  noSort?: boolean,
};

function useDebounce<K, T extends Promise<K>>(delay: number, callback: () => T): () => T {
  const timeout = React.useRef<NodeJS.Timeout | null>(null);
  const callbackRef = React.useRef(callback);
  React.useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  return React.useCallback(() => {
    if (timeout.current) {
      clearTimeout(timeout.current);
    }

    return new Promise((resolve, reject) => {
      timeout.current = setTimeout(async () => {
        try {
          resolve(await callbackRef.current());
        } catch (err) {
          reject(err);
        }
      }, delay);
    }) as unknown as T;
  }, [delay]);
}

export const DEFAULT_PATIENT_LIST_LIMIT = 100;

export const PatientTable = (props: {
  filterObj: Record<string, object | boolean>,
  allFilterValues: PatientListFiltersType,
  setAllFilterValues: (allFilterValues: PatientListFiltersType) => void,
  extraColumns?: Array<TableColumn<ClinicianPatientsTableResponseRow>>,
  q: any,
  setQ: (q: any) => void,
  limit?: number,
}) => {
  const { q, setQ, setAllFilterValues } = props;
  const history = useHistory();
  const clinicService = ClinicService();
  const clinicianReq = useQuery(['clinician'], async () => {
    return clinicService.getClinician();
  });
  const { clinician, token } = useStore();
  const {
    setPatient,
    setPatientSummary,
    setGoals,
    setWater,
    setEmotion,
    setHEI,
    setDash,
    setNutrients,
    setPatientSubscriptions,
  } = useStoreDispatch();

  const limit = Number(props.limit) || DEFAULT_PATIENT_LIST_LIMIT;

  const clinics = clinician.clinics as ClinicResponse[];
  const hospitalCustomColumns = {} as Record<string, HospitalPatientCustomField>;
  clinics?.forEach(clinic => {
    const hospitalCustomFields = clinicGetHospitalPatientCustomFields(clinic);
    if (!hospitalCustomFields) {
      return;
    }
    hospitalCustomFields.forEach(field => {
      hospitalCustomColumns[field.field] = field;
    });
  });
  const { t } = useTranslation();
  const COLUMNS: Array<TableColumn<ClinicianPatientsTableResponseRow>> = [
    {
      field: 'hospital_patient_external_id' as any,
      header: t('External ID'),
      render: (row) => (
        <span>
          {row.hospital_attributes?.patient_external_id}
        </span>
      ),
      hidden: !clinics.find(x => x.flags?.hospital_patient_has_external_id),
      noSort: true,
    },

    ...Object.values(hospitalCustomColumns).map(field => ({
      field: 'hospital_patient_custom_fields:' + field.field as any,
      header: field.label,
      render: (row) => (
        <span>
          {row.hospital_attributes?.patient_custom_fields?.[field.field]}
        </span>
      ),
      noSort: true,
    })),

    {
      field: 'last_name',
      header: t('Name'),
      render: (row) => (
        <span>
          {row.last_name}, {row.first_name} <span style={{ opacity: 0.6 }}>#{row.patient_id}</span>{' '}
          {!!row.invitation_status && row.invitation_status !== 'accepted' && (
            <span style={{ opacity: 0.6 }}>({InvitationStatus[row.invitation_status]})</span>
          )}
        </span>
      ),
    },

    {
      field: 'email',
      header: t('Email'),
    },

    {
      field: 'phone_number',
      header: t('Phone Number'),
    },

    {
      field: 'next_report_date',
      header: t('Next Report'),
    },

    {
      field: 'last_completed_report_date',
      header: t('Last Report'),
      render: (row) => (
        <span>
          <Link key="reports" to="/patient/reports">{row.last_completed_report_date}</Link>
        </span>
      ),
    },

    {
      field: 'last_meal_date',
      header: t('Last Meal'),
    },
  ];
  const columns = COLUMNS.filter(x => !x.hidden).concat(...(props.extraColumns || []));

  const queryOrder = !q.order ? null : {
    column: q.order.substring(1),
    dir: q.order.substring(0, 1) as '+' | '-',
  };
  const [sortColumn, _setSortColumn] = React.useState(queryOrder);

  const handleColumnHeaderClick = (column: TableColumn<ClinicianPatientsTableResponseRow>) => {
    if (!column.field) {
      return;
    }

    if (column.noSort) {
      return;
    }

    if (!sortColumn || sortColumn.column != column.field) {
      _setSortColumn({
        column: column.field,
        dir: '+',
      });
      setAllFilterValues({ ...props.allFilterValues, page: 1 });
      return;
    }

    if (sortColumn.dir == '+') {
      _setSortColumn({
        column: column.field,
        dir: '-',
      });
    } else {
      _setSortColumn(null);
    }
    setAllFilterValues({ ...props.allFilterValues, page: 1 });
  };

  const tableParams: Partial<PatientReportApiAppApiClinicReportClinicianPatientsTableRequest> = {
    order: sortColumn ? `${sortColumn.dir}${sortColumn.column}` : '',
    filter: JSON.stringify(props.filterObj),
    limit: limit,
    offset: q.page ? ((q.page - 1) * limit).toString() : '0',
  };

  useEffect(() => {
    const filterValues = Object.fromEntries(
      Object.keys(props.allFilterValues).map(key => {
        if (props.allFilterValues[key]?.length === 0) {
          return [key, undefined];
        }
        return [key, props.allFilterValues[key]];
      }),
    );
    setQ({
      ...filterValues,
      order: tableParams.order || undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.allFilterValues, tableParams.order]);

  // Clinician-scoped, multi-hospital roster (FOOD-841) — no hospital_id, so it
  // spans every hospital the clinician belongs to. Same swap FOOD-837 made for
  // clinics. Requires MNT #3978 deployed.
  const params = {
    clinician_id: clinicianReq.data?.id || 0,
    ...tableParams,
  };

  const getTableData = useDebounce(props.filterObj.name ? 250 : 0, async () => {
    if (clinicianReq.isLoading) {
      // See note at line 10
      // return new Promise(() => {}) as Promise<AppApiClinicReportClinicianPatientsTable200Response>;
      return new Promise(() => {}) as Promise<AppApiClinicReportPatientClinicInvitationTable200Response>;
    }
    if (clinicianReq.isError) {
      throw clinicianReq.error;
    }

    const authConfig = {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        order: params.order,
        filter: params.filter,
        limit: params.limit,
        offset: params.offset,
      },
    };
    const patientClinicInvitationRes = params.offset === '0'
      ? await axios.get<AppApiClinicReportPatientClinicInvitationTable200Response>(
          `${hostUrl}/api/clinicians/${params.clinician_id}/patients/clinic-invitation-table`,
          authConfig,
        )
      : { data: { rows: [] } };

    const clinicianPatientTableRes = await axios.get<AppApiClinicReportPatientClinicInvitationTable200Response>(
      `${hostUrl}/api/clinicians/${params.clinician_id}/patients/table`,
      authConfig,
    );

    return {
      rows: [
        ...patientClinicInvitationRes.data.rows,
        ...clinicianPatientTableRes.data.rows,
      ],
      total_rows: clinicianPatientTableRes.data.total_rows,
      has_more: clinicianPatientTableRes.data.has_more,
    };
  });

  const tableQuery = useQuery(['patient-table', params, params.filter], getTableData);

  const resetPatient = () => {
    setPatient(null);
    setPatientSummary(null);
    setGoals([]);
    setNutrients([]);
    setWater([]);
    setHEI([]);
    setDash([]);
    setEmotion([]);
    setPatientSubscriptions([]);
  };

  const columnSortIcon = (column: TableColumn<ClinicianPatientsTableResponseRow>) => {
    if (!sortColumn || sortColumn.column != column.field) {
      /* eslint-disable-next-line i18next/no-literal-string */
      return 'sort';
    }
    /* eslint-disable-next-line i18next/no-literal-string */
    return sortColumn.dir == '+' ? 'sort-up' : 'sort-down';
  };

  return (
    <div>
      <Table hover style={{ width: '100%' }}>
        <thead>
          <tr>
            {columns.map((column, index) => (
              <th
                key={index}
                onClick={() => handleColumnHeaderClick(column)}
                style={{ cursor: column.field ? 'pointer' : 'default' }}
              >
                {column.header}
                {column.field && !column.noSort && (
                  <>
                    {' '}
                    <MDBIcon icon={columnSortIcon(column)} />
                  </>
                )}
              </th>
            ))}
          </tr>
        </thead>
        {tableQuery.isLoading && (
          <tbody>
            <tr>
              <td colSpan={99}>
                <LoadingSpinner />
              </td>
            </tr>
          </tbody>
        )}
        {tableQuery.isError && (
          <tbody>
            <tr>
              <td colSpan={99}>
                <Trans>Unexpected error loading patient list</Trans>: {'' + tableQuery.error}
              </td>
            </tr>
          </tbody>
        )}
        {!tableQuery.isLoading && tableQuery?.data?.rows?.length == 0 && (
          <tbody>
            <tr>
              <td colSpan={99}>
                <i>
                  <Trans>No patients found</Trans>
                </i>
              </td>
            </tr>
          </tbody>
        )}
        {!tableQuery.isLoading && (
          <tbody>
            {tableQuery?.data?.rows?.map((row, index) => {
              const defaultOnClick = (e) => {
                resetPatient();
                if (e.ctrlKey || e.type === 'contextmenu') {
                  window.open(`/patient/${idToStr(row.patient_id)}`, '_blank');
                  e.preventDefault();
                } else {
                  history.push(`/patient/${idToStr(row.patient_id)}`);
                }
              };
              const userCanClick = !row.invitation_status || row.invitation_status === 'accepted';
              return (
                <tr key={index}>
                  {columns.map((column, index) => (
                    <td
                      key={index}
                      style={{ cursor: column.onClick !== null && userCanClick ? 'pointer' : 'default' }}
                      onClick={!userCanClick || column.onClick === null
                        ? null
                        : column.onClick === undefined
                        ? (e) => defaultOnClick(e)
                        : () => column.onClick(row)}
                      onContextMenu={column.onClick === null
                        ? null
                        : column.onClick === undefined
                        ? (e) => defaultOnClick(e)
                        : () => column.onClick(row)}
                    >
                      {column.render ? column.render(row) : row[column.field]}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        )}
      </Table>
      {!tableQuery.isLoading && tableQuery?.data?.rows && tableQuery.data.total_rows > limit && (
        <Pagination
          page={q.page || 1}
          limit={limit}
          totalRows={tableQuery.data.total_rows}
          allFilterValues={props.allFilterValues}
          setAllFilterValues={props.setAllFilterValues}
        />
      )}
    </div>
  );
};

const MAX_PAGES_DISPLAY = 5;

const Pagination = (props: {
  page: number,
  limit: number,
  totalRows: number,
  allFilterValues: PatientListFiltersType,
  setAllFilterValues: (allFilterValues: PatientListFiltersType) => void,
}) => {
  const { page, limit, totalRows, allFilterValues, setAllFilterValues } = props;
  const { t } = useTranslation();

  const totalPages = Math.ceil(totalRows / limit);
  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, totalRows);

  const minPage = (() => {
    if (page + MAX_PAGES_DISPLAY - 1 > totalPages) {
      return Math.max(1, totalPages - MAX_PAGES_DISPLAY + 1);
    }
    return Math.max(1, page - Math.floor(MAX_PAGES_DISPLAY / 2));
  })();

  return (
    <div className="paginationFooter">
      <span className="paginationMeta">
        {t('Showing {{start}} to {{end}} of {{total}} entries', { start, end, total: totalRows })}
      </span>
      <div className="paginationControls">
        <button
          className="paginationBtn"
          disabled={page === 1}
          onClick={() => setAllFilterValues({ ...allFilterValues, page: page - 1 })}
        >
          <Trans>Previous</Trans>
        </button>
        {Array.from(
          { length: Math.min(MAX_PAGES_DISPLAY, totalPages) },
          (_, index) => {
            const pageNum = minPage + index;
            const isActive = page === pageNum;
            return (
              <button
                key={index}
                className={`paginationBtn paginationPage${isActive ? ' paginationActive' : ''}`}
                onClick={() => !isActive && setAllFilterValues({ ...allFilterValues, page: pageNum })}
              >
                {pageNum}
              </button>
            );
          },
        )}
        <button
          className="paginationBtn"
          disabled={page === totalPages}
          onClick={() => setAllFilterValues({ ...allFilterValues, page: page + 1 })}
        >
          <Trans>Next</Trans>
        </button>
      </div>
    </div>
  );
};
